import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { 
  Settings, 
  ArrowLeft, 
  Save, 
  Type, 
  FileText,
  Edit3,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const AdminContentEditor = () => {
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('homepage');

  // États pour tous les textes du site
  const [siteContent, setSiteContent] = useState({
    // Page d'accueil
    homepage: {
      heroTitle: 'AutoSpa 77',
      heroSubtitle: 'Centre de Detailing',
      heroDescription: 'Redonnez vie à votre véhicule avec nos services de detailing haut de gamme. Plus de 10 ans d\'expérience à votre service.',
      bannerTitle: 'L\'Excellence du Detailing',
      bannerSubtitle: 'Votre véhicule mérite le meilleur traitement',
      ctaTitle: 'Prêt à redonner vie à votre véhicule ?',
      ctaDescription: 'Réservez dès maintenant ou contactez-nous pour un devis personnalisé'
    },
    // Page Tarifs
    tarifs: {
      heroTitle: 'Nos Tarifs AutoSpa 77',
      heroDescription: 'Des formules transparentes et des services personnalisés pour tous vos besoins de detailing automobile.',
      formulesTitle: 'Nos Formules Principales',
      formulesDescription: 'Deux formules complètes pour répondre à tous vos besoins de nettoyage',
      detailingTitle: 'Detailing Personnalisé',
      detailingDescription: 'Services à la carte pour une prestation sur-mesure',
      specialTitle: 'Services Spécialisés',
      specialDescription: 'Prestations expertes sur devis personnalisé'
    },
    // Page Galerie
    galerie: {
      heroTitle: 'Galerie de Nos Réalisations',
      heroDescription: 'Découvrez la transformation spectaculaire de nos clients grâce à nos services de detailing professionnel',
      ctaTitle: 'Votre véhicule mérite le même traitement',
      ctaDescription: 'Contactez-nous pour découvrir ce que nous pouvons faire pour votre automobile'
    },
    // Page Contact
    contact: {
      heroTitle: 'Contactez AutoSpa 77',
      heroDescription: 'Une question ? Un devis ? N\'hésitez pas à nous contacter',
      formTitle: 'Envoyez-nous un message',
      directTitle: 'Contact direct',
      faqTitle: 'Questions fréquentes'
    },
    // Informations générales
    general: {
      phone: '06 22 36 01 90',
      email: 'contact.autospa77@gmail.com',
      instagram: 'https://www.instagram.com/auto_spa_77/',
      whatsappMessage: 'Bonjour AutoSpa 77, je souhaite avoir des informations sur vos services de detailing automobile.'
    }
  });

  // Rediriger si pas connecté
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const sections = [
    { key: 'homepage', label: 'Page d\'accueil', icon: FileText },
    { key: 'tarifs', label: 'Page Tarifs', icon: Type },
    { key: 'galerie', label: 'Page Galerie', icon: Edit3 },
    { key: 'contact', label: 'Page Contact', icon: Type },
    { key: 'general', label: 'Infos générales', icon: Settings }
  ];

  const handleContentChange = (section, field, value) => {
    setSiteContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Ici vous pourrez ajouter l'appel API pour sauvegarder
      // await api.updateSiteContent(siteContent);
      
      localStorage.setItem('autospa_site_content', JSON.stringify(siteContent));
      
      toast({
        title: "Contenu sauvegardé !",
        description: "Tous les textes ont été mis à jour avec succès.",
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la sauvegarde.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderSectionEditor = (sectionKey) => {
    const section = siteContent[sectionKey];
    
    return (
      <div className="space-y-4">
        {Object.entries(section).map(([fieldKey, value]) => {
          const isTextarea = fieldKey.includes('Description') || fieldKey.includes('Message') || value.length > 100;
          
          return (
            <div key={fieldKey}>
              <Label htmlFor={`${sectionKey}-${fieldKey}`} className="capitalize">
                {fieldKey.replace(/([A-Z])/g, ' $1').toLowerCase()}
              </Label>
              {isTextarea ? (
                <Textarea
                  id={`${sectionKey}-${fieldKey}`}
                  value={value}
                  onChange={(e) => handleContentChange(sectionKey, fieldKey, e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              ) : (
                <Input
                  id={`${sectionKey}-${fieldKey}`}
                  value={value}
                  onChange={(e) => handleContentChange(sectionKey, fieldKey, e.target.value)}
                  className="mt-1"
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Link to="/admin/dashboard">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Tableau de bord
                </Button>
              </Link>
              <div className="flex items-center space-x-3">
                <Type className="w-6 h-6 text-blue-600" />
                <h1 className="text-2xl font-bold text-gray-900">Éditeur de contenu</h1>
              </div>
            </div>
            <Button onClick={handleSave} disabled={isSaving} className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Sauvegarde...' : 'Sauvegarder tout'}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Navigation des sections */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Sections du site</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-1">
                  {sections.map((section) => {
                    const IconComponent = section.icon;
                    return (
                      <button
                        key={section.key}
                        onClick={() => setActiveSection(section.key)}
                        className={`w-full text-left px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors ${
                          activeSection === section.key ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600' : 'text-gray-700'
                        }`}
                      >
                        <IconComponent className="w-4 h-4" />
                        <span className="text-sm">{section.label}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Éditeur de contenu */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Edit3 className="w-5 h-5 text-blue-600" />
                  <span>
                    {sections.find(s => s.key === activeSection)?.label}
                  </span>
                </CardTitle>
                <p className="text-sm text-gray-600">
                  Modifiez les textes de cette section du site
                </p>
              </CardHeader>
              <CardContent>
                {renderSectionEditor(activeSection)}
              </CardContent>
            </Card>

            {/* Aperçu (optionnel) */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Aperçu des modifications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Les modifications seront visibles sur le site après sauvegarde.
                    Utilisez le bouton "Sauvegarder tout" pour appliquer tous les changements.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContentEditor;